import { Icon } from '@design-system';
import { useTranslation } from 'next-i18next';
import React from 'react';

import {
  ETH_BLOCKCHAIN,
  PLACE_HOLDER_IMAGE,
  POLYGON_BLOCKCHAIN,
} from '../../../../constants';
import {
  ImageStoryblok,
  NftCounterpartStoryblok,
  VideoStoryblok,
} from '../../../../types/generated-storyblok';
import BackgroundVideo from '../../../BackgroundVideo';
import { Image } from '../../../Image';
import Drawer from '../../../Layout/Drawer';

interface CounterpartFigureProps {
  media: ImageStoryblok | (ImageStoryblok | VideoStoryblok)[];
  title?: string;
  hasLink?: boolean;
}

const CounterpartFigure: React.FC<CounterpartFigureProps> = ({
  hasLink,
  media,
  title,
}) => (
  <figure className="flex flex-col space-y-3">
    {Array.isArray(media) ? (
      media.map((mediaItem: ImageStoryblok | VideoStoryblok) => {
        if (mediaItem.component === 'Video') {
          return (
            <div className="aspect-[1/1] w-full">
              <BackgroundVideo
                className="overflow-hidden rounded-sm"
                cover={mediaItem.coverImage}
                vimeo={mediaItem.vimeo}
              />
            </div>
          );
        }

        return (
          <div className="aspect-[1/1] w-full">
            <Image
              alt={title || mediaItem.image.alt || '—'}
              hoverEffect
              shape="rounded"
              src={mediaItem.image.filename || PLACE_HOLDER_IMAGE}
            />
          </div>
        );
      })
    ) : (
      <div className="aspect-[1/1] w-full">
        <Image
          alt={title || media.image.alt || '—'}
          hoverEffect
          shape="rounded"
          src={media.image.filename || PLACE_HOLDER_IMAGE}
        />
      </div>
    )}

    <figcaption>
      <p className="flex text-xs text-neutral-4">
        {hasLink && (
          <Icon className="ml-1" name="ic_arrow_top_right" size="xs" />
        )}
        {title || '—'}
      </p>
    </figcaption>
  </figure>
);

interface ProductCounterpartsDrawerProps {
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  counterparts: NftCounterpartStoryblok[];
  overlayOtherDrawers?: boolean;
}

export const ProductCounterpartsDrawer: React.FC<
  ProductCounterpartsDrawerProps
> = ({ counterparts, isVisible, overlayOtherDrawers, setIsVisible }) => {
  const { t } = useTranslation(['product']);
  const closeDrawer = () => {
    setIsVisible(false);
  };

  const blockchains = [ETH_BLOCKCHAIN, POLYGON_BLOCKCHAIN];
  const getBlockchainNameByValue = (value: number | string) => {
    const blockchain = blockchains.find(
      (blockchain) => blockchain.value === value,
    );
    return blockchain ? blockchain.name : '';
  };

  const chainIdsArray = [
    ...new Set(
      counterparts
        .map((item) =>
          item.chainId ? getBlockchainNameByValue(item.chainId.toString()) : '',
        )
        .filter((value) => value.trim() !== ''),
    ),
  ];
  const tokenStandardsArray = [
    ...new Set(
      counterparts
        .map((item) => item.tokenStandard)
        .filter((value) => value.toString().trim() !== ''),
    ),
  ];

  return (
    <Drawer
      onClose={closeDrawer}
      overlayOtherDrawers={overlayOtherDrawers}
      shown={isVisible}
      title={t('counterpart.drawer.title')}
    >
      <div className="mx-auto max-w-[400px] pb-12">
        <div className="flex flex-col space-y-6">
          <div className="flex flex-col gap-y-1">
            <h3 className="text-md font-bold md:text-lg">
              {t('counterpart.drawer.title')}
            </h3>
            <p> {t('counterpart.drawer.description')}</p>
          </div>

          <div className="flex flex-col space-y-6">
            {counterparts.map((counterpart) => (
              <>
                {counterpart?.link !== undefined &&
                'url' in counterpart.link ? (
                  <a
                    key={counterpart._uid}
                    href={counterpart.link.url}
                    rel="noreferrer"
                    target="_blank"
                  >
                    <CounterpartFigure
                      hasLink
                      media={counterpart.media}
                      title={counterpart.title}
                    />
                  </a>
                ) : (
                  <CounterpartFigure
                    key={counterpart._uid}
                    media={counterpart.media}
                    title={counterpart.title}
                  />
                )}
              </>
            ))}
          </div>

          {(chainIdsArray.length > 0 || tokenStandardsArray.length > 0) && (
            <div className="flex flex-col space-y-3 rounded-md border border-divider/20 bg-white p-4 md:space-y-4">
              {chainIdsArray.length > 0 && (
                <div className="flex flex-col md:flex-row md:space-x-3">
                  <p className="flex-1 truncate">
                    {chainIdsArray.length > 1
                      ? t('counterpart.drawer.tokens.blockchain.multiple')
                      : t('counterpart.drawer.tokens.blockchain.single')}
                  </p>
                  <p className="shrink-0 text-neutral-4">
                    {chainIdsArray.join(', ')}
                  </p>
                </div>
              )}
              {tokenStandardsArray.length > 0 && (
                <div className="flex flex-col md:flex-row md:space-x-3">
                  <p className="flex-1 truncate">
                    {tokenStandardsArray.length > 1
                      ? t('counterpart.drawer.tokens.tokenStandard.multiple')
                      : t('counterpart.drawer.tokens.tokenStandard.single')}
                  </p>
                  <p className="shrink-0 uppercase text-neutral-4">
                    {tokenStandardsArray.join(', ')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};
